import type { IncidentReport } from '@lmc/ts-common'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { incidentReportsConstants } from 'locale/incidentReports'

const DATE = 'DD-MM-YYYY'

const format = (value: string | Date | null | undefined, template: string) =>
  value && dayjs(value).isValid() ? dayjs(value).format(template) : null

/** An instant's date as `DD-MM-YYYY`, in the viewer's time. */
export const formatDate = (value: string | Date | null | undefined) =>
  format(value, DATE)

/** An instant as `DD-MM-YYYY HH:mm`, in the viewer's time. */
export const formatDateTime = (value: string | Date | null | undefined) =>
  format(value, `${DATE} HH:mm`)

/** An instant's time as `HH:mm`, in the viewer's time. */
export const formatTime = (value: string | Date | null | undefined) =>
  format(value, 'HH:mm')

/**
 * A span's times as "start – end", or the start alone without an end. An end
 * on a later day carries its date, so 23:50 – 00:10 reads as overnight.
 */
export const formatTimeRange = (
  t: TFunction,
  startIso: string | null | undefined,
  endIso: string | null | undefined
) => {
  const start = formatTime(startIso)
  const end =
    formatDate(startIso) === formatDate(endIso)
      ? formatTime(endIso)
      : formatDateTime(endIso)

  return start && end
    ? t(incidentReportsConstants.CASE_V2_VIEW_TIME_RANGE, { end, start })
    : start
}

/**
 * A date-only answer (stored as midnight UTC of the day picked) as `DD-MM-YYYY`.
 * Read from the date part, not as an instant, or it shows the day before for a
 * viewer west of UTC.
 */
export const formatDateOnly = (iso: string | null | undefined) =>
  iso ? dayjs(iso.slice(0, 10)).format(DATE) : null

/** A yes/no answer's label key; unanswered is unrecorded, never "No". */
export const yesNoKey = (value: boolean | null | undefined) =>
  value === undefined || value === null
    ? null
    : value
      ? incidentReportsConstants.CASE_V2_YES
      : incidentReportsConstants.CASE_V2_NO

/** The case's attachments, then the log's photos, then its video. */
export const incidentAttachments = ({
  attachments,
  log
}: Pick<IncidentReport, 'attachments' | 'log'>) => [
  ...[...(attachments ?? []), log.picture1, log.picture2, log.picture3].filter(
    (url): url is string => Boolean(url)
  ),
  ...(log.videoFile
    ? [{ isVideo: true, thumbnail: log.videoThumbnail, url: log.videoFile }]
    : [])
]
